/**
 * The road graph of a region (docs/archive/OPEN_WORLD.md §2.2, §4.2; lane E) and long routes over it.
 *
 * `findPath` searches one window of at most 256 m (path.ts); past that it returns null. A long route
 * (auto-walk to a far marker, the coach between stations) walks the local window to a road, follows
 * the road graph, and walks a window again to the goal: `findRoute`.
 *
 * The graph is built from the sketch's `roads` and `stations` (data/regions/<id>.ts):
 * - a node at every road vertex (its bends), vertices of different roads within `JOIN` merged into
 *   one (the junctions), and a road's end that lies on another road's segment split into it (a tee);
 * - a road edge per segment, with its length;
 * - a node per station, and link edges (walked through a window, not paved) between road ends, and
 *   from each station to the road ends within `LINK` and any node within `STATION_NEAR` (the gaps
 *   through towns, villages and compounds).
 *
 * Pure and deterministic: no DOM, timers or randomness. A link a window cannot walk on a given map
 * is dropped for that map (and the route searched again), which is a property of the map alone.
 */
import { ZONES } from '../data/zones';
import type { GameMap } from '../map';
import { findPath, nearestFloor, type Waypoint } from '../path';
import type { RegionSketch } from './sketch';
import { REGIONS } from './zone';

/** Vertices closer than this (m) are one junction. */
export const JOIN = 1.5;
/** A road end this near (m) another road's segment joins it there (a tee). */
export const TEE = 10;
/** Link edges (window walks) join road ends and stations to nodes this near (m). */
export const LINK = 250;
/** A station also joins any road node this near (m), not only road ends. */
export const STATION_NEAR = 120;
/** How far (m) a window leg may reach from the start or goal to the road (fits one path window). */
export const REACH = 180;
/** Off-road legs (windows, links) cost this much more per metre than the road (detours). */
export const OFF_ROAD = 1.25;
/** Spacing (m) of the waypoints laid along a road. */
export const ROAD_STEP = 4;

export interface RoadNode {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /** The sketch station at this node, if any. */
  readonly station?: string;
}

export interface RoadEdge {
  readonly id: number;
  readonly a: number;
  readonly b: number;
  /** Straight length (m): a road edge is one straight segment, a link a straight estimate. */
  readonly len: number;
  /** The road's id, or null for a link (walked through a window). */
  readonly road: string | null;
}

export interface RoadGraph {
  readonly nodes: readonly RoadNode[];
  readonly edges: readonly RoadEdge[];
  /** Edge ids at each node. */
  readonly adj: readonly (readonly number[])[];
  /** Node of each station id. */
  readonly stations: ReadonlyMap<string, number>;
  /** Sum of the road edges (m), links not counted. */
  readonly roadLength: number;
}

const d2 = (ax: number, ay: number, bx: number, by: number): number => Math.hypot(ax - bx, ay - by);

/** The projection of (x, y) on segment a→b: its parameter t in [0, 1], the point and its distance. */
function project(ax: number, ay: number, bx: number, by: number, x: number, y: number) {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
  const px = ax + dx * t;
  const py = ay + dy * t;
  return { t, px, py, d: d2(px, py, x, y) };
}

/** The road graph of a sketch (see the file's comment). */
export function buildRoadGraph(s: Pick<RegionSketch, 'roads' | 'stations'>): RoadGraph {
  // Tees: a road's end on another road's segment becomes a vertex of that road.
  const roads = s.roads.map((r) => ({ id: r.id, pts: r.pts.map((p) => [p[0], p[1]] as [number, number]) }));
  for (const r of roads)
    for (const end of [r.pts[0]!, r.pts[r.pts.length - 1]!])
      for (const o of roads) {
        if (o === r) continue;
        for (let k = 0; k + 1 < o.pts.length; k++) {
          const [ax, ay] = o.pts[k]!;
          const [bx, by] = o.pts[k + 1]!;
          const pr = project(ax, ay, bx, by, end[0], end[1]);
          if (pr.d > TEE || d2(ax, ay, pr.px, pr.py) <= JOIN || d2(bx, by, pr.px, pr.py) <= JOIN) continue;
          // The end snaps onto the road it meets.
          end[0] = pr.px;
          end[1] = pr.py;
          o.pts.splice(k + 1, 0, [pr.px, pr.py]);
          break;
        }
      }

  const nodes: RoadNode[] = [];
  const edges: RoadEdge[] = [];
  const adj: number[][] = [];
  const nodeAt = (x: number, y: number): number => {
    for (const n of nodes) if (d2(n.x, n.y, x, y) <= JOIN) return n.id;
    nodes.push({ id: nodes.length, x, y });
    adj.push([]);
    return nodes.length - 1;
  };
  const linked = (a: number, b: number): boolean =>
    adj[a]!.some((e) => edges[e]!.a === b || edges[e]!.b === b);
  const edge = (a: number, b: number, road: string | null): void => {
    if (a === b || linked(a, b)) return;
    const n = nodes[a]!;
    const m = nodes[b]!;
    edges.push({ id: edges.length, a, b, len: d2(n.x, n.y, m.x, m.y), road });
    adj[a]!.push(edges.length - 1);
    adj[b]!.push(edges.length - 1);
  };

  const ends: number[] = [];
  let roadLength = 0;
  for (const r of roads) {
    const ids = r.pts.map(([x, y]) => nodeAt(x, y));
    for (let k = 0; k + 1 < ids.length; k++) edge(ids[k]!, ids[k + 1]!, r.id);
    ends.push(ids[0]!, ids[ids.length - 1]!);
  }
  for (const e of edges) roadLength += e.len;

  // Stations: their own nodes.
  const stations = new Map<string, number>();
  for (const st of s.stations) {
    nodes.push({ id: nodes.length, x: st.x, y: st.y, station: st.id });
    adj.push([]);
    stations.set(st.id, nodes.length - 1);
  }

  // Links: from each road end (one that meets no other road) and each station to the nodes in reach.
  const loose = [...new Set(ends)].filter((n) => adj[n]!.length === 1);
  const from = [...loose, ...stations.values()];
  for (const a of from) {
    const na = nodes[a]!;
    for (const nb of nodes) {
      if (nb.id === a || d2(na.x, na.y, nb.x, nb.y) > LINK) continue;
      // Road ends join other road ends and stations; a station also joins any node near it.
      const d = d2(na.x, na.y, nb.x, nb.y);
      if (!loose.includes(nb.id) && !nb.station && !(na.station && d <= STATION_NEAR)) continue;
      edge(a, nb.id, null);
    }
  }
  return { nodes, edges, adj, stations, roadLength };
}

const graphs = new Map<string, RoadGraph>();

/** A region's road graph by its id, built once per process. */
export function regionRoads(id: string): RoadGraph | null {
  let g = graphs.get(id);
  if (!g) {
    const def = REGIONS[id];
    if (!def) return null;
    g = buildRoadGraph(def.sketch);
    graphs.set(id, g);
  }
  return g;
}

/** The road graph of a map: its zone's region's on a region map (chunked), else null. */
export function roadGraphOf(m: GameMap): RoadGraph | null {
  if (!m.chunks) return null;
  const id = ZONES[m.zone]?.region;
  return id ? regionRoads(id) : null;
}

// ───────────────────────── search ─────────────────────────

/** A way onto (or off) the graph: a node in reach, or a point on a road edge in reach. */
interface Anchor {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  /** The nodes it reaches, each with the cost along the road from the anchor and that edge. */
  readonly to: readonly { node: number; cost: number }[];
  /** For a point on a road edge: that edge (its length from a). */
  readonly edge?: number;
  readonly s?: number;
}

function anchors(g: RoadGraph, x: number, y: number, skip: ReadonlySet<string>): Anchor[] {
  const out: Anchor[] = [];
  for (const n of g.nodes) {
    const key = `n${n.id}`;
    if (!skip.has(key) && d2(n.x, n.y, x, y) <= REACH)
      out.push({ key, x: n.x, y: n.y, to: [{ node: n.id, cost: 0 }] });
  }
  for (const e of g.edges) {
    if (!e.road) continue;
    const key = `e${e.id}`;
    if (skip.has(key)) continue;
    const a = g.nodes[e.a]!;
    const b = g.nodes[e.b]!;
    const pr = project(a.x, a.y, b.x, b.y, x, y);
    if (pr.d > REACH || pr.t <= 0 || pr.t >= 1) continue;
    const s = pr.t * e.len;
    out.push({
      key,
      x: pr.px,
      y: pr.py,
      edge: e.id,
      s,
      to: [
        { node: e.a, cost: s },
        { node: e.b, cost: e.len - s },
      ],
    });
  }
  return out;
}

/** A plan: anchors, then the node sequence and the edges between (empty when it stays on one edge). */
interface Plan {
  start: Anchor;
  goal: Anchor;
  nodes: number[];
  edges: number[];
  cost: number;
}

function plan(
  g: RoadGraph,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  starts: readonly Anchor[],
  goals: readonly Anchor[],
  dead: ReadonlySet<number>,
): Plan | null {
  const N = g.nodes.length;
  const dist = new Float64Array(N).fill(Infinity);
  const via = new Int32Array(N).fill(-1); // edge into the node
  const src = new Int32Array(N).fill(-1); // start anchor index of a source node
  for (let k = 0; k < starts.length; k++) {
    const a = starts[k]!;
    const off = d2(sx, sy, a.x, a.y) * OFF_ROAD;
    for (const t of a.to)
      if (off + t.cost < dist[t.node]!) {
        dist[t.node] = off + t.cost;
        src[t.node] = k;
        via[t.node] = -1;
      }
  }
  // Dijkstra (a few dozen nodes: a scan beats a heap); ties go to the lower id.
  const done = new Uint8Array(N);
  for (;;) {
    let u = -1;
    for (let n = 0; n < N; n++) if (!done[n] && dist[n]! < Infinity && (u < 0 || dist[n]! < dist[u]!)) u = n;
    if (u < 0) break;
    done[u] = 1;
    for (const eid of g.adj[u]!) {
      if (dead.has(eid)) continue;
      const e = g.edges[eid]!;
      const v = e.a === u ? e.b : e.a;
      const c = dist[u]! + e.len * (e.road ? 1 : OFF_ROAD);
      if (c < dist[v]!) {
        dist[v] = c;
        via[v] = eid;
        src[v] = src[u]!;
      }
    }
  }
  let best: Plan | null = null;
  for (const goal of goals) {
    const off = d2(tx, ty, goal.x, goal.y) * OFF_ROAD;
    for (const t of goal.to) {
      const c = dist[t.node]! + t.cost + off;
      if (!(c < (best?.cost ?? Infinity))) continue;
      const nodes: number[] = [];
      const edges: number[] = [];
      let n = t.node;
      while (via[n]! >= 0) {
        nodes.push(n);
        edges.push(via[n]!);
        const e = g.edges[via[n]!]!;
        n = e.a === n ? e.b : e.a;
      }
      nodes.push(n);
      nodes.reverse();
      edges.reverse();
      best = { start: starts[src[t.node]!]!, goal, nodes, edges, cost: c };
    }
  }
  // Both on one road edge: along it directly.
  for (const a of starts)
    for (const b of goals) {
      if (a.edge === undefined || a.edge !== b.edge) continue;
      const c = (d2(sx, sy, a.x, a.y) + d2(tx, ty, b.x, b.y)) * OFF_ROAD + Math.abs(a.s! - b.s!);
      if (c < (best?.cost ?? Infinity)) best = { start: a, goal: b, nodes: [], edges: [], cost: c };
    }
  return best;
}

/** Lays waypoints along a straight piece of road from (ax, ay) to (bx, by), every `ROAD_STEP` m. */
function lay(out: Waypoint[], ax: number, ay: number, bx: number, by: number): void {
  const l = d2(ax, ay, bx, by);
  if (l < 0.01) return;
  const n = Math.max(1, Math.ceil(l / ROAD_STEP));
  for (let k = 1; k <= n; k++) out.push([ax + ((bx - ax) * k) / n, ay + ((by - ay) * k) / n]);
}

/** Per map: links a window could not walk, and the window paths of those it could (by "edge>dir"). */
interface MapRoads {
  dead: Set<number>;
  legs: Map<string, Waypoint[]>;
}
const perMap = new WeakMap<GameMap, MapRoads>();
const stateOf = (m: GameMap): MapRoads => {
  let s = perMap.get(m);
  if (!s) perMap.set(m, (s = { dead: new Set(), legs: new Map() }));
  return s;
};

/** How many times a route is searched again after a window leg fails. */
const RETRIES = 16;

/**
 * A long route from (sx, sy) to (tx, ty) on a region map: a window to the road, the road graph, a
 * window to the goal. Waypoints as `findPath`'s (the last is the exact goal), or null when there is
 * no road graph (not a region map), nothing in reach, or no way found.
 */
export function findRoute(m: GameMap, sx: number, sy: number, tx: number, ty: number): Waypoint[] | null {
  const g = roadGraphOf(m);
  if (!g) return null;
  const st = stateOf(m);
  const skipS = new Set<string>();
  const skipG = new Set<string>();
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    const p = plan(g, sx, sy, tx, ty, anchors(g, sx, sy, skipS), anchors(g, tx, ty, skipG), st.dead);
    if (!p) return null;
    const out: Waypoint[] = [];
    // The window onto the road.
    if (d2(sx, sy, p.start.x, p.start.y) > 1) {
      const leg = findPath(m, sx, sy, p.start.x, p.start.y);
      if (!leg) {
        skipS.add(p.start.key);
        continue;
      }
      out.push(...leg);
    }
    let x = p.start.x;
    let y = p.start.y;
    if (!p.nodes.length) {
      lay(out, x, y, p.goal.x, p.goal.y);
    } else {
      const first = g.nodes[p.nodes[0]!]!;
      lay(out, x, y, first.x, first.y);
      let failed = false;
      for (let k = 0; k < p.edges.length; k++) {
        const e = g.edges[p.edges[k]!]!;
        const a = g.nodes[p.nodes[k]!]!;
        const b = g.nodes[p.nodes[k + 1]!]!;
        if (e.road) {
          lay(out, a.x, a.y, b.x, b.y);
          continue;
        }
        const key = `${e.id}>${b.id}`;
        let leg = st.legs.get(key);
        if (!leg) {
          const found = findPath(m, a.x, a.y, b.x, b.y);
          if (!found) {
            st.dead.add(e.id);
            failed = true;
            break;
          }
          leg = found;
          st.legs.set(key, leg);
        }
        for (const w of leg) out.push([w[0], w[1]]);
      }
      if (failed) continue;
      const last = g.nodes[p.nodes[p.nodes.length - 1]!]!;
      lay(out, last.x, last.y, p.goal.x, p.goal.y);
    }
    x = p.goal.x;
    y = p.goal.y;
    // The window off the road to the goal.
    if (d2(x, y, tx, ty) > 1) {
      const leg = findPath(m, x, y, tx, ty);
      if (!leg) {
        skipG.add(p.goal.key);
        continue;
      }
      out.push(...leg);
    } else out.push([tx, ty]);
    return out;
  }
  return null;
}

/** A walk from here to there: one window when it can (`findPath`), else the road (`findRoute`). */
export function routeTo(m: GameMap, sx: number, sy: number, tx: number, ty: number): Waypoint[] | null {
  return findPath(m, sx, sy, tx, ty) ?? findRoute(m, sx, sy, tx, ty) ?? relay(m, sx, sy, tx, ty);
}

/** The longest hop of a relay (tiles) and how many hops one walk may take. */
const RELAY_HOP = 180;
const RELAY_HOPS = 8;

/**
 * A walk too long for one window and off the roads (across the new Yanyu, whose 600 m has none of the region's roads):
 * windows of `findPath` strung along the straight line, each hop ending on the floor nearest the line.
 */
function relay(m: GameMap, sx: number, sy: number, tx: number, ty: number): Waypoint[] | null {
  const out: Waypoint[] = [];
  let x = sx;
  let y = sy;
  for (let hop = 0; hop < RELAY_HOPS; hop++) {
    const d = Math.hypot(tx - x, ty - y);
    const last = d <= RELAY_HOP;
    let gx = tx;
    let gy = ty;
    if (!last) {
      const q = nearestFloor(m, x + ((tx - x) / d) * RELAY_HOP, y + ((ty - y) / d) * RELAY_HOP, 12);
      if (!q) return null;
      [gx, gy] = q;
    }
    const leg = findPath(m, x, y, gx, gy);
    if (!leg || !leg.length) return null;
    out.push(...leg);
    if (last) return out;
    [x, y] = leg[leg.length - 1]!;
  }
  return null;
}

/** Length (m) of a walk from (sx, sy) along its waypoints. */
export function walkLength(sx: number, sy: number, path: readonly Waypoint[]): number {
  let l = 0;
  let x = sx;
  let y = sy;
  for (const [px, py] of path) {
    l += d2(x, y, px, py);
    x = px;
    y = py;
  }
  return l;
}

/**
 * The road length (m) between two of a region's stations by the graph alone (links and the legs off
 * the road at their straight lengths): what fares and the destination list go by. Null when unjoined.
 */
export function stationDistance(g: RoadGraph, a: string, b: string): number | null {
  const na = g.stations.get(a);
  const nb = g.stations.get(b);
  if (na === undefined || nb === undefined) return null;
  if (na === nb) return 0;
  const N = g.nodes.length;
  const dist = new Float64Array(N).fill(Infinity);
  const done = new Uint8Array(N);
  dist[na] = 0;
  for (;;) {
    let u = -1;
    for (let n = 0; n < N; n++) if (!done[n] && dist[n]! < Infinity && (u < 0 || dist[n]! < dist[u]!)) u = n;
    if (u < 0 || u === nb) break;
    done[u] = 1;
    for (const eid of g.adj[u]!) {
      const e = g.edges[eid]!;
      const v = e.a === u ? e.b : e.a;
      if (dist[u]! + e.len < dist[v]!) dist[v] = dist[u]! + e.len;
    }
  }
  return dist[nb]! < Infinity ? dist[nb]! : null;
}
